import { nanoid } from "nanoid";
import { ReactElement, Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import Component404 from "../components/Component404/Component404";
import { useAppSelector } from "../store/reduxHooks";
import { SELECT_PROFILE_ROLE } from "../store/selectors/profileSelectors";
import { SELECT_ROUTINIG } from "../store/selectors/routingSelector";
import { USER } from "../types/indexTypeEnum";
import NotAuthorizeRoute from "../utils/HOC/NonAuthorizeRoute";
import PrivateRoute from "../utils/HOC/PrivateRoute";
import { PageMainRoute } from "../views/indexPage";

const PageApplications = lazy(() =>
  import("../views/pageApplications/PageApplications").then((m) => ({
    default: m.PageApplications,
  }))
);
const PageApplication = lazy(() =>
  import("../views/pageApplication/PageApplication").then((m) => ({
    default: m.PageApplication,
  }))
);
const PageApplicationCreate = lazy(() =>
  import("../views/pageApplicationCreate/PageApplicationCreate").then((m) => ({
    default: m.PageApplicationCreate,
  }))
);
const PageApplicationAdditional = lazy(() =>
  import("../views/pageApplicationAdditional/PageApplicationAdditional").then(
    (m) => ({ default: m.PageApplicationAdditional })
  )
);
const PageBanks = lazy(() =>
  import("../views/pageBanks/PageBanks").then((m) => ({
    default: m.PageBanks,
  }))
);
const PageBankConditions = lazy(() =>
  import("../views/pageBankConditions/PageBankConditions").then((m) => ({
    default: m.PageBankConditions,
  }))
);
const PageListAgent = lazy(() =>
  import("../views/pageListAgent/PageListAgent").then((m) => ({
    default: m.PageListAgent,
  }))
);
const PageListClient = lazy(() =>
  import("../views/pageListClient/PageListClient").then((m) => ({
    default: m.PageListClient,
  }))
);
const PageListClientForAgent = lazy(() =>
  import("../views/pageListClientForAgent/PageListClientForAgent").then(
    (m) => ({ default: m.PageListClientForAgent })
  )
);
const PageListManager = lazy(() =>
  import("../views/pageListManager/PageListManager").then((m) => ({
    default: m.PageListManager,
  }))
);
const PageProfile = lazy(() =>
  import("../views/pageProfile/PageProfile").then((m) => ({
    default: m.PageProfile,
  }))
);
const PageReport = lazy(() =>
  import("../views/pageReport/PageReport").then((m) => ({
    default: m.PageReport,
  }))
);
const PageUserCreate = lazy(() =>
  import("../views/pageUserCreate/PageUserCreate").then((m) => ({
    default: m.PageUserCreate,
  }))
);
const PageAgent = lazy(() =>
  import("../views/pageAgent/PageAgent").then((m) => ({ default: m.PageAgent }))
);
const PageClient = lazy(() =>
  import("../views/pageClient/PageClient").then((m) => ({
    default: m.PageClient,
  }))
);
const PageManager = lazy(() =>
  import("../views/pageManager/PageManager").then((m) => ({
    default: m.PageManager,
  }))
);
const PageListAgentIndividual = lazy(() =>
  import("../views/PageListAgentIndividual/PageListAgentIndividual").then(
    (m) => ({ default: m.PageListAgentIndividual })
  )
);
const PageAgentIndividual = lazy(() =>
  import("../views/pageAgentIndividual/PageAgentIndividual").then((m) => ({
    default: m.PageAgentIndividual,
  }))
);
const PageDocumentsSigning = lazy(() =>
  import("../views/pageDocumentsSigning/PageDocumentsSigning").then((m) => ({
    default: m.PageDocumentsSigning,
  }))
);
const PageRequiringSigningDocuments = lazy(() =>
  import(
    "../views/pageRequiringSigningDocuments/pageRequiringSigningDocuments"
  ).then((m) => ({ default: m.PageRequiringSigningDocuments }))
);
const PageLogin = lazy(() =>
  import("../views/pageLogin/PageLogin").then((m) => ({
    default: m.PageLogin,
  }))
);
const PageRegisterAgent = lazy(() =>
  import("../views/pageRegisterAgent/pageRegisterAgent").then((m) => ({
    default: m.PageRegisterAgent,
  }))
);
const PageRegisterClient = lazy(() =>
  import("../views/pageRegisterClient/pageRegisterClient").then((m) => ({
    default: m.PageRegisterClient,
  }))
);
const PageRecoveryPassword = lazy(() =>
  import("../views/pageRecoveryPassword/pageRecoveryPassword").then((m) => ({
    default: m.PageRecoveryPassword,
  }))
);
const PageSelectRegistration = lazy(() =>
  import("../views/pageSelectRegistration/pageSelectRegistration").then(
    (m) => ({ default: m.PageSelectRegistration })
  )
);
const PageAgreementDataProcessingDocument = lazy(() =>
  import(
    "../views/pageAgreementDocument/PageAgreementDataProcessingDocument"
  ).then((m) => ({ default: m.PageAgreementDataProcessingDocument }))
);
const PagePrivacyPolicyDocument = lazy(() =>
  import(
    "../views/pagePrivacyPolicyDocument/pagePrivacyPolicyDocument"
  ).then((m) => ({ default: m.PagePrivacyPolicyDocument }))
);
const PageUnsubscribe = lazy(() =>
  import("../views/pageUnsubscribe/PageUnsubscribe").then((m) => ({
    default: m.PageUnsubscribe,
  }))
);
const PageAnalytics = lazy(() =>
  import("../views/pageAnalytics/PageAnalytics").then((m) => ({
    default: m.PageAnalytics,
  }))
);
const PageSurvey = lazy(() =>
  import("../views/pageSurvey/PageSurvey").then((m) => ({
    default: m.PageSurvey,
  }))
);
const PageSurveyAdmin = lazy(() =>
  import("../views/pageSurveyAdmin/PageSurveyAdmin").then((m) => ({
    default: m.PageSurveyAdmin,
  }))
);
const PageNews = lazy(() =>
  import("../views/pageNews/PageNews").then((m) => ({
    default: m.PageNews,
  }))
);
const PageNewsAdmin = lazy(() =>
  import("../views/pageNewsAdmin/PageNewsAdmin").then((m) => ({
    default: m.PageNewsAdmin,
  }))
);
const PageManagerAssignments = lazy(() =>
  import("../views/pageManagerAssignments/PageManagerAssignments").then((m) => ({
    default: m.PageManagerAssignments,
  }))
);

interface IPages {
  [key: string]: ReactElement;
}

const NotAuthorizeRoutes = [
  {
    element: (
      <NotAuthorizeRoute>
        <PageLogin />
      </NotAuthorizeRoute>
    ),
    path: "/login",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageRegisterAgent />
      </NotAuthorizeRoute>
    ),
    path: "/registerAgent",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageRegisterClient />
      </NotAuthorizeRoute>
    ),
    path: "/registerClient",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageRecoveryPassword />
      </NotAuthorizeRoute>
    ),
    path: "/recoveryPassword",
  },
  {
    element: (
      <NotAuthorizeRoute>
        <PageSelectRegistration />
      </NotAuthorizeRoute>
    ),
    path: "/selectRegistration",
  },
  {
    element: <PageClient />,
    path: "/client/:id",
  },
  {
    element: <PageManager />,
    path: "/manager/:id",
  },
  {
    element: <PageAgent />,
    path: "/agent/:id",
  },
];

export function AppRoutes(): ReactElement {
  const role = useAppSelector(SELECT_PROFILE_ROLE);
  const routingSlice = useAppSelector(SELECT_ROUTINIG);

  const ROUTS_ELEMENT: IPages = {
    PageApplications: <PageApplications />,
    PageBanks: <PageBanks />,
    PageBankConditions: <PageBankConditions />,
    PageListClient:
      role === USER.AGENT_SIMPLE ? (
        <PageListClientForAgent />
      ) : (
        <PageListClient />
      ),
    PageListAgent: <PageListAgent />,
    PageReport: <PageReport />,
    PageAnalytics: <PageAnalytics />,
    PageSurvey: <PageSurvey />,
    PageSurveyAdmin: <PageSurveyAdmin />,
    PageNews: <PageNews />,
    PageNewsAdmin: <PageNewsAdmin />,
    PageProfile: <PageProfile />,
    ApplicationCreate: <PageApplicationCreate />,
    Application: <PageApplication />,
    PageListManager: <PageListManager />,
    PageManagerAssignments: <PageManagerAssignments />,
    PageUserCreate: <PageUserCreate />,
    PageClient: <PageClient />,
    PageAgent: <PageAgent />,
  };

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route
          path={"/documents-signing"}
          element={
            <PrivateRoute>
              <PageDocumentsSigning />
            </PrivateRoute>
          }
        />

        <Route path={"/"} element={<PageMainRoute routes={routingSlice} />}>
          <Route
            path={"/client/:id"}
            element={
              <PrivateRoute>
                <PageClient />
              </PrivateRoute>
            }
          />

          <Route
            path={"/agent/:id"}
            element={
              <PrivateRoute>
                <PageAgent />
              </PrivateRoute>
            }
          />
          <Route
            path={"/manager/:id"}
            element={
              <PrivateRoute>
                <PageManager />
              </PrivateRoute>
            }
          />

          <Route
            path="/list-agent-individual"
            element={
              <PrivateRoute>
                <PageListAgentIndividual />
              </PrivateRoute>
            }
          />

          <Route
            path="/agent-individual"
            element={
              <PrivateRoute>
                <PageAgentIndividual />
              </PrivateRoute>
            }
          />

          <Route
            path={
              "/system-bank/create-data-to-bank/:applicationId/application-bank/:applicationBankId"
            }
            element={
              <PrivateRoute>
                <PageApplicationAdditional />
              </PrivateRoute>
            }
          />

          <Route
            path="/application-create"
            element={
              <PrivateRoute>
                <PageApplicationCreate />
              </PrivateRoute>
            }
          />
          {routingSlice?.map((routeGroup) => {
            const { routes } = routeGroup;
            return routes?.map((route) => {
              const element = ROUTS_ELEMENT[route.element];
              const ID = nanoid();
              const link = route.to;
              if (link === "application") {
                return (
                  <Route key={ID} path={route.to} element={element}>
                    <Route
                      key={ID + "application"}
                      path={":id"}
                      element={
                        <PrivateRoute>
                          <PageApplication />
                        </PrivateRoute>
                      }
                    />
                  </Route>
                );
              }
              return (
                <Route
                  key={ID}
                  path={"/" + route.to}
                  element={<PrivateRoute>{element}</PrivateRoute>}
                />
              );
            });
          })}

          <Route
            path="/requiringDocs"
            element={<PageRequiringSigningDocuments />}
          />
        </Route>
        <Route
          path="*"
          element={
            <PrivateRoute>
              <PageMainRoute routes={routingSlice} />
            </PrivateRoute>
          }
        >
          <Route
            path="*"
            element={
              <PrivateRoute>
                <Component404 />
              </PrivateRoute>
            }
          />
        </Route>

        {NotAuthorizeRoutes.map(({ path, element }) => (
          <Route key={path} path={path} element={element} />
        ))}

        <Route
          path="/agreementDataProcessing"
          element={<PageAgreementDataProcessingDocument />}
        />
        <Route
          path="/agreementPrivacyPolicy"
          element={<PagePrivacyPolicyDocument />}
        />
        <Route path="/unsubscribe/:token" element={<PageUnsubscribe />} />
      </Routes>
    </Suspense>
  );
}
